import React, { useCallback, useEffect, useState } from 'react'
import SearchBar from 'src/components/common/search-and-filters/subcomponents/SearchBar'
import { apiURI } from 'src/lib/utils/constants'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import { getRequest } from 'src/lib/core/api-client'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { successToast, errorToast } from 'src/lib/utils/toasters'
import { customerPerformanceData, rewardKPIMetadata } from 'src/lib/utils/rewards'
import RewardsAnalyticsKPI from 'src/components/rewards/setup/RewardsAnalyticsKPI'
import { currencyFormatter } from 'src/lib/utils/formatters'
import CustomerTransactionTable from 'src/components/rewards/setup/CustomerTransactionTable'
import { customerTransactionDataAdapter } from 'src/lib/utils/adapters'
import { ArrowRight, ArrowLeft } from 'react-feather'
import FloRadioGroup from 'src/components/common/form-elements/FloRadioGroup'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { postRequest } from 'src/lib/core/api-client'
import { useLocale } from 'src/lib/hooks/useLocal'

const ISSUER_ID = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

const CustomerSpecificDetails: React.FC<{}> = ({}) => {
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [customerTransactionData, setCustomerTransactionData] = useState<any>()
  const [openCustomerDialog, setOpenCustomerDialog] = useState<boolean>(false)
  const [openCustomerTopupDialog, setOpenCustomeTopupDialog] = useState<boolean>(false)
  const [isCustomerTransactionsLoading, setIsCustomerTransactionsLoading] = useState<boolean>(false)

  const {
    getters: { merchant }
  } = useMerchant()
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()

  const [txnType, setTxnType] = useState<'CREDIT' | 'DEBIT'>('CREDIT')
  const [creditConfig, setCreditConfig] = useState<{
    amount?: number
    expiry?: number
    reason?: string
  }>({})
  const [debitConfig, setDebitConfig] = useState<{
    amount?: number
    reason?: string
  }>({})

  const getCustomerDetails = async () => {
    if (!phoneNumber) return
    try {
      setIsCustomerTransactionsLoading(true)
      setLoaderProperties({ isLoaderVisible: true, message: `Fetching customer's details` })
      const customerData = await getRequest(
        `/issuer/${ISSUER_ID}/merchant/${
          merchant?.accountId
        }/analytics/user-spend-report?phone-number=${encodeURIComponent(phoneNumber)}`,
        apiURI.REWARDS_ENGINE
      )
      if (!(customerData as any)?.data?.data) {
        const wrongPhonenumber = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`
        errorToast(
          `Customer not found. Please ensure phone number includes country code (Eg: ${wrongPhonenumber})`,
          6000
        )
        return
      }
      setCustomerTransactionData((customerData as any)?.data?.data)
      setOpenCustomerDialog(true)
    } catch (e) {
      console.error(e)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
      setIsCustomerTransactionsLoading(false)
    }
  }

  const CustomerDialogContent = () => {
    if (!customerTransactionData) return <></>
    return (
      <div className='flex flex-col gap-4 p-4 rounded-b-3xl bg-gray-lightest'>
        <div className='flex flex-row items-center justify-between w-full h-full p-3 bg-white rounded-lg border-1 border-gray-light'>
          <div className='flex flex-col'>
            <p className='font-semibold text-coal-dark'>{customerTransactionData?.data?.name}</p>
            <p className='text-sm text-gray-dark'>{customerTransactionData?.phone_number}</p>
          </div>
          <div>
            <button
              className='flex items-center gap-1 px-3 py-2 text-sm font-semibold border rounded-lg text-coal-dark max-w-fit'
              onClick={() => {
                setOpenCustomerDialog(false)
                setOpenCustomeTopupDialog(true)
              }}
            >
              Add or remove credit
              <ArrowRight className='h-4 w-4 stroke-[2.5px]' color={'#4D4D4D'} />
            </button>
          </div>
        </div>
        <div className='flex flex-col w-full gap-4'>
          <ul className='flex flex-row w-full gap-2 flex-wrap'>
            {Object.entries(customerTransactionData ?? {}).map(([key, value]) => {
              const matchedKPI = customerPerformanceData.find((kpi: any) => kpi.key === key)
              if (matchedKPI) {
                return (
                  <li key={key} className='grow basis-0'>
                    <RewardsAnalyticsKPI
                      description={matchedKPI.displayText}
                      header={currencyFormatter(value as any)}
                    />
                  </li>
                )
              }
            })}
          </ul>
        </div>

        <div className='relative flex w-full'>
          <CustomerTransactionTable
            value={customerTransactionDataAdapter(customerTransactionData?.transactions) ?? []}
            isLoading={isCustomerTransactionsLoading}
            isPaginationEnabled={true}
          />
        </div>
      </div>
    )
  }

  const handleCustomerTopup = async () => {
    try {
      setLoaderProperties({ isLoaderVisible: true, message: `Upading customer's walet` })
      if (txnType === 'CREDIT') {
        const payload = {
          oid: phoneNumber,
          reference_id: '',
          event_type: 'MANUAL',
          reference: 'MANUAL_ADJUST',
          amount: creditConfig.amount,
          expiry_at: creditConfig.expiry ? Date.now() + creditConfig.expiry * 86400000 : null,
          reason: creditConfig?.reason ?? ''
        }
        await postRequest(
          `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/user-wallet/credit-transaction`,
          apiURI.REWARDS_ENGINE,
          payload
        )
      } else {
        const payload = {
          oid: phoneNumber,
          transaction_reference: 'MANUAL_ADJUST',
          amount: debitConfig.amount,
          reference_id: '',
          reason: debitConfig?.reason ?? ''
        }
        const response: any = await postRequest(
          `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/user-wallet/debit-transaction`,
          apiURI.REWARDS_ENGINE,
          payload
        )
        const debitError = response?.error ?? response?.data?.error
        if (debitError) {
          errorToast(typeof debitError === 'string' ? debitError : debitError?.message ?? t('something_went_wrong'))
          return
        }
      }
      await getCustomerDetails()
      successToast(t('wallet_update_success'))
      resetCreditDebitConfig()
      setOpenCustomeTopupDialog(false)
      setOpenCustomerDialog(true)
    } catch (e) {
      errorToast(t('something_went_wrong'))
      console.error(e)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const walletBalance = customerTransactionData?.total_wallet_balance ?? 0
  const isDebitAmountExceedingBalance =
    txnType === 'DEBIT' && Boolean(debitConfig?.amount) && debitConfig.amount! > walletBalance

  const resetCreditDebitConfig = useCallback(() => {
    setCreditConfig({ ...creditConfig, expiry: undefined, amount: undefined, reason: undefined })
    setDebitConfig({ ...debitConfig, amount: undefined, reason: undefined })
  }, [])

  const customerTopupDialogContent = useCallback(() => {
    const handleCreditDebitConfigChange = (inputType: keyof typeof creditConfig, value: string | number) => {
      if (!inputType) return

      if (txnType === 'CREDIT') {
        setCreditConfig((oldValues: any) => ({ ...oldValues, [inputType]: value }))
      } else {
        setDebitConfig((oldValues: any) => ({ ...oldValues, [inputType]: value }))
      }
    }

    return (
      <div className='flex flex-col gap-4 p-4 bg-gray-lightest'>
        <div className='p-4 bg-white rounded-lg border-1 border-gray-light'>
          <FloRadioGroup
            selectedOption={txnType}
            options={[
              { label: 'Credit', valueType: 'CREDIT' },
              { label: 'Debit', valueType: 'DEBIT' }
            ]}
            groupName={'txn-type-configuration-radio-group'}
            setSelectedOption={(selectedOption: any) => {
              setTxnType(selectedOption)
            }}
          />
        </div>
        <div className='p-4 bg-white rounded-lg border-1 border-gray-light'>
          {txnType === 'CREDIT' && (
            <div className='flex flex-col gap-4'>
              <div className='flex flex-row gap-4'>
                <div className='flex flex-col items-start justify-between w-full'>
                  <p className='text-sm font-medium text-coal-light'>Amount to credit</p>
                  <InputTextField
                    inputValue={creditConfig.amount}
                    textFieldId='credit-amount'
                    startIcon={<>₹</>}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleCreditDebitConfigChange('amount', parseInt(event.target.value))
                    }
                    inputType='number'
                    isFullWidth={true}
                  />
                </div>
                <div className='flex flex-col items-start justify-between w-full'>
                  <p className='text-sm font-medium text-coal-light'>Set expiry</p>
                  <InputTextField
                    inputValue={creditConfig.expiry}
                    textFieldId='credit-expiry'
                    endIcon={<>days</>}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleCreditDebitConfigChange('expiry', parseInt(event.target.value))
                    }
                    inputPlaceholder={'Never, click to edit'}
                    inputType='number'
                    isFullWidth={true}
                  />
                </div>
              </div>
              <div>
                <div className='flex flex-col items-start justify-between w-full'>
                  <p className='text-sm font-medium text-coal-light'>Reason for credit</p>
                  <InputTextField
                    inputValue={creditConfig.reason}
                    textFieldId='credit-reason'
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleCreditDebitConfigChange('reason', event.target.value)
                    }
                    inputPlaceholder={'Add reason for credit'}
                    inputType='text'
                    isFullWidth={true}
                  />
                </div>
              </div>
            </div>
          )}
          {txnType === 'DEBIT' && (
            <>
              <div className='flex flex-col gap-4'>
                <div>
                  <div className='flex flex-col items-start justify-between w-full'>
                    <p className='text-sm font-medium text-coal-light'>Amount to debit</p>
                    <InputTextField
                      inputValue={debitConfig.amount}
                      textFieldId='debit-amount'
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleCreditDebitConfigChange('amount', parseInt(event.target.value))
                      }
                      inputType='number'
                      isFullWidth={true}
                      startIcon={<>– ₹</>}
                      errorStatus={isDebitAmountExceedingBalance}
                      errorMessage={`Amount exceeds available wallet balance of ${currencyFormatter(walletBalance)}`}
                    />
                  </div>
                </div>
                <div>
                  <div className='flex flex-col items-start justify-between w-full'>
                    <p className='text-sm font-medium text-coal-light'>Reason for debit</p>
                    <InputTextField
                      inputValue={debitConfig.reason}
                      textFieldId='debit-reason'
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleCreditDebitConfigChange('reason', event.target.value)
                      }
                      inputPlaceholder={'Add reason for debit'}
                      inputType='text'
                      isFullWidth={true}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }, [creditConfig, debitConfig, txnType, walletBalance, isDebitAmountExceedingBalance])

  const isCustomerTopupButtonEnabled = useCallback(() => {
    if (txnType === 'CREDIT') {
      return Boolean(creditConfig?.amount)
    }
    if (txnType === 'DEBIT') {
      return Boolean(debitConfig?.amount) && !isDebitAmountExceedingBalance
    }
    return false
  }, [creditConfig, debitConfig, txnType, isDebitAmountExceedingBalance])

  return (
    <>
      <div>
        <SearchBar
          label={'Customer phone number'}
          placeholder={'Search for a customer by phone number'}
          handleSearchSubmit={getCustomerDetails}
          searchText={phoneNumber}
          onChange={setPhoneNumber}
          clearSearch={() => setPhoneNumber('')}
          customCss='!w-full'
          autoFocus={false}
        />
      </div>

      {Boolean(openCustomerDialog) && (
        <FloGenericDialog
          width={600}
          isOpen={openCustomerDialog}
          handleClose={() => setOpenCustomerDialog(false)}
          content={<CustomerDialogContent />}
          title={`Showing transaction details for ${phoneNumber}`}
          referrer='rewards-customer-details'
          contentCss={{ padding: 0 }}
          primaryBtnEnabled={false}
        />
      )}

      {Boolean(openCustomerTopupDialog) && (
        <FloGenericDialog
          width={600}
          isOpen={openCustomerTopupDialog}
          handleClose={() => {
            setOpenCustomeTopupDialog(false)
            setOpenCustomerDialog(true)
          }}
          content={customerTopupDialogContent()}
          title={
            <div className='flex items-center gap-2'>
              <ArrowLeft
                className='h-4 w-4 stroke-[2.5px] cursor-pointer'
                color={'#4D4D4D'}
                onClick={() => {
                  setOpenCustomeTopupDialog(false)
                  setOpenCustomerDialog(true)
                }}
              />
              Add or remove credit for {phoneNumber}
            </div>
          }
          referrer='rewards-customer-details'
          contentCss={{ padding: 0 }}
          jobButtonText={`${
            txnType === 'CREDIT'
              ? `Credit ${creditConfig.amount ? `₹${creditConfig.amount}` : ''} to wallet`
              : `Debit ${debitConfig.amount ? `₹${debitConfig.amount}` : ''} from wallet`
          }`}
          jobButtonDisabled={!isCustomerTopupButtonEnabled()}
          handleJob={handleCustomerTopup}
        />
      )}
    </>
  )
}

export default React.memo(CustomerSpecificDetails)
